import {
  Activity,
  ArrowRightCircle,
  FileText,
  FolderKanban,
  Gauge,
  Globe2,
  Link2,
  Mail,
  MapPinned,
  Play,
  Route,
  Search,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";

export type TabKey = "dashboard" | "opportunities" | "approvals" | "executions";

export type ModuleItem = {
  key: string;
  label: string;
  description: string;
  provider: string;
  status: string;
  icon: LucideIcon;
} & (
  | {
      kind: "external";
      href: string;
    }
  | {
      kind: "embedded";
      href: string;
      targetOrigin: string;
    }
  | {
      kind: "internal";
      tab: TabKey;
    }
);

const OPEN_SEO_BASE_URL = "https://openseo.theportlandcompany.com";
const KEYWORD_DENSITY_APP_URL = "https://keyworddensity.theportlandcompany.com";

export const moduleItems: ModuleItem[] = [
  {
    key: "keyword-research",
    label: "Keyword Research",
    description:
      "Launch the deployed OpenSEO workspace for keyword discovery, prioritization, and topic research.",
    provider: "OpenSEO",
    status: "deployed",
    icon: Search,
    kind: "external",
    href: `${OPEN_SEO_BASE_URL}/keywords`,
  },
  {
    key: "domain-insights",
    label: "Domain Insights",
    description:
      "Launch the deployed OpenSEO workspace for domain visibility, keyword movement, and search footprint analysis.",
    provider: "OpenSEO",
    status: "deployed",
    icon: Globe2,
    kind: "external",
    href: `${OPEN_SEO_BASE_URL}/domain`,
  },
  {
    key: "backlinks",
    label: "Backlinks",
    description:
      "Launch the deployed OpenSEO workspace for backlink discovery, trend analysis, and referring domain review.",
    provider: "OpenSEO",
    status: "deployed",
    icon: Link2,
    kind: "external",
    href: `${OPEN_SEO_BASE_URL}/backlinks`,
  },
  {
    key: "openseo-site-audits",
    label: "OpenSEO",
    description:
      "Launch the deployed OpenSEO site-audit workflow for crawl diagnostics, Lighthouse checks, and technical issue review.",
    provider: "OpenSEO",
    status: "deployed",
    icon: Search,
    kind: "external",
    href: `${OPEN_SEO_BASE_URL}/audit`,
  },
  {
    key: "keyword-density",
    label: "Keyword Density",
    description:
      "Launch the embedded keyword-density analyzer for full-site indexing, per-page phrase density, and site-wide n-gram summaries.",
    provider: "TPC SEO",
    status: "integrated",
    icon: Search,
    kind: "embedded",
    href: `${KEYWORD_DENSITY_APP_URL}/embed`,
    targetOrigin: KEYWORD_DENSITY_APP_URL,
  },
  {
    key: "analytics-tracking",
    label: "Analytics Tracking",
    description:
      "Use Umami for traffic, session flow, and top-level site analytics until this moves in-platform.",
    provider: "Umami",
    status: "connected",
    icon: Activity,
    kind: "external",
    href: "https://analytics.theportlandcompany.com",
  },
  {
    key: "event-tracking",
    label: "Event Tracking",
    description:
      "Use the Umami workspace for custom event auditing, event naming, and event-level verification.",
    provider: "Umami",
    status: "connected",
    icon: Play,
    kind: "external",
    href: "https://analytics.theportlandcompany.com",
  },
  {
    key: "conversion-tracking",
    label: "Conversion Tracking",
    description:
      "Use Umami for conversion and goal tracking until the approval loop owns cross-channel attribution directly.",
    provider: "Umami",
    status: "connected",
    icon: ShieldCheck,
    kind: "external",
    href: "https://analytics.theportlandcompany.com",
  },
  {
    key: "keyword-tracking",
    label: "Keyword Range Tracking + Geofencing",
    description:
      "Configured to the local SERPBear instance for rank monitoring, keyword ranges, and location-aware tracking.",
    provider: "SERPBear",
    status: "configured",
    icon: MapPinned,
    kind: "external",
    href: "http://localhost:0233",
  },
  {
    key: "dashboard",
    label: "Dashboard",
    description:
      "Organization inventory, project structure, domains, social accounts, and directory listings.",
    provider: "Platform",
    status: "in_platform",
    icon: FolderKanban,
    kind: "internal",
    tab: "dashboard",
  },
  {
    key: "target-audience",
    label: "Target Audience",
    description:
      "Define site-specific demographic profile details so strategy, content, and execution stay aligned to the intended audience.",
    provider: "Audience strategy",
    status: "in_platform",
    icon: Users,
    kind: "internal",
    tab: "dashboard",
  },
  {
    key: "content",
    label: "Content",
    description:
      "Page-level content assessments, topical gaps, on-page recommendations, and publish-ready content opportunities.",
    provider: "Assessment engine",
    status: "in_platform",
    icon: FileText,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "meta-data",
    label: "Meta Data",
    description:
      "Title tags, meta descriptions, heading structure, canonicals, schema, and search appearance improvements.",
    provider: "Search standards",
    status: "in_platform",
    icon: Search,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "speed-performance",
    label: "Speed & Performance",
    description:
      "Core Web Vitals, Lighthouse-backed performance findings, and execution-ready fixes for page speed issues.",
    provider: "Lighthouse",
    status: "in_platform",
    icon: Gauge,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "navigation-landing-pages",
    label: "Navigation & Landing Pages",
    description:
      "Internal linking, page hierarchy, crawl paths, and landing page opportunities driven by site assessment data.",
    provider: "Site audit",
    status: "in_platform",
    icon: Route,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "internal-linking",
    label: "Internal Linking",
    description:
      "Cross-page linking opportunities, anchor text improvements, and crawl-path reinforcement driven by site assessment data.",
    provider: "Site audit",
    status: "in_platform",
    icon: Link2,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "content-creation",
    label: "On-Site Content Creation",
    description:
      "Managed in-platform through action plans that create or expand on-site content through website APIs.",
    provider: "Website APIs",
    status: "in_platform",
    icon: Mail,
    kind: "internal",
    tab: "opportunities",
  },
  {
    key: "reporting-dashboard",
    label: "Reporting & Dashboard",
    description:
      "Managed in-platform with approval, execution, and reporting views fed by Supabase-backed workflow history.",
    provider: "Platform",
    status: "in_platform",
    icon: ArrowRightCircle,
    kind: "internal",
    tab: "executions",
  },
];
